import * as Sentry from '@sentry/react';

// Error tracking for the web client (OBS-01, VEK-35). This module is the only place that knows
// about the Sentry SDK; the rest of the client calls the three verbs below, which are safe no-ops
// when no DSN is configured — `captureException` and `setUser` do nothing without a client.
//
// The DSN and the environment name are inlined at build time (Vite `VITE_APP_*`), so a build is
// either reporting or silent for its whole life. `release` is the short git SHA the footer shows
// (`__GIT_COMMIT__`, see vite.config.mts), so an issue in Sentry lines up with the deployed stamp.

const dsn = import.meta.env.VITE_APP_SENTRY_DSN || '';

// `ApiError` (api/vektra/client.ts) is recognised by shape rather than imported: client.ts imports
// `captureError` from here, and importing the class back would make the pair a circular module.
function isExpectedApiError(exception: unknown): boolean {
  if (typeof exception !== 'object' || exception === null) return false;
  const { name, status } = exception as { name?: unknown; status?: unknown };
  return name === 'ApiError' && typeof status === 'number' && status < 500;
}

export function initErrorTracking(): void {
  if (!dsn) return;

  Sentry.init({
    dsn,
    release: __GIT_COMMIT__,
    environment: import.meta.env.VITE_APP_SENTRY_ENVIRONMENT || 'unknown',
    // Errors only. No tracing, no replay, no PII (no IP, no request/response bodies, no email).
    sendDefaultPii: false,
    tracesSampleRate: 0,
    maxBreadcrumbs: 30,
    // An ApiError below 500 is an expected business response — session expiry, validation,
    // permission, conflict, rate limit — and the pages already render it. Whatever path brings
    // one here (an uncaught mutateAsync rejection, a render that rethrows), it never costs quota.
    beforeSend(event, hint) {
      return isExpectedApiError(hint.originalException) ? null : event;
    }
  });
}

// `fingerprint` groups a failure by its kind rather than by the stack that hit it (OBS-02): a network
// failure or an upstream 502 from ten endpoints during one outage is one issue, not ten.
export function captureError(error: unknown, context?: Record<string, unknown>, fingerprint?: string[]): void {
  if (!context && !fingerprint) {
    Sentry.captureException(error);
    return;
  }
  Sentry.captureException(error, { extra: context, fingerprint });
}

export function setTrackedUser(user: { id: string; businessAccountId?: string } | null): void {
  // An undefined tag value is dropped when the event is serialised, so clearing is a real removal
  // rather than the string "null" reaching the tracker.
  Sentry.setUser(user ? { id: user.id } : null);
  Sentry.setTag('businessAccountId', user?.businessAccountId);
}
